import React, { useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { deviceDataApi } from '../../api/device-data.api';
import Banner from '../../components/general/Banner';
import DownloadButton from '../../components/general/DownloadButton';
import EmptyPeriod from '../../components/general/EmptyPeriod';
import RangeToolbar from '../../components/general/RangeToolbar';
import Spinner from '../../components/general/Spinner';
import StatusPill from '../../components/general/StatusPill';
import RawDataTable from '../../components/geo/RawDataTable';
import RouteMap from '../../components/geo/RouteMap';
import Page from '../../components/layout/Page';
import PageHeader from '../../components/layout/PageHeader';
import { useCsvDownload } from '../../hooks/useCsvDownload';
import { useDeviceDetail } from '../../hooks/useDeviceDetail';
import { useTranslation } from '../../i18n/i18n';
import { DeviceType } from '../../models/device';
import { Period, presetPeriod } from '../../models/time-range';
import { geoListUrl, weatherDetailUrl } from '../../routes.constants';
import { formatDateTime, formatTime } from '../../utils/format';
import { routeDistanceKm } from '../../utils/geo';
import { GeoSample, toGeoSample } from '../../utils/payload';
import './GeoDetailPage.scss';

const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();

const GeoDetailPage = () => {
  const { deviceUuid = '' } = useParams();
  const { t } = useTranslation('geo');
  const { t: tc } = useTranslation('common');
  const [period, setPeriod] = useState<Period>(() => presetPeriod('24h'));
  const [selected, setSelected] = useState<number | null>(null);
  const { device, latest, window, loading, error, reload } = useDeviceDetail(
    deviceUuid,
    period.from,
    period.to,
    deviceDataApi.getGeoByTimeRange,
  );
  const csv = useCsvDownload(deviceUuid, device?.name ?? '');

  const samples = useMemo(
    () => (window?.readings ?? []).map(toGeoSample).filter((s): s is GeoSample => s !== undefined),
    [window],
  );

  const changePeriod = (p: Period) => {
    setSelected(null);
    setPeriod(p);
  };

  if (device && device.type === DeviceType.WEATHER) return <Navigate to={weatherDetailUrl(device.uuid)} replace />;

  const name = device?.name ?? '';
  const first = samples[0]?.time;
  const last = samples[samples.length - 1]?.time;
  const meta = [
    { label: tc('uuid'), value: deviceUuid },
    ...(latest?.topic ? [{ label: tc('topic'), value: latest.topic }] : []),
  ];
  const routeTitle =
    first && last
      ? t('route.title', {
          from: formatDateTime(first),
          to: sameDay(first, last) ? formatTime(last) : formatDateTime(last),
        })
      : t('route.titleEmpty');

  return (
    <Page variant="detail">
      <PageHeader
        crumbs={[{ label: tc('nav.geo'), to: geoListUrl }, { label: name || deviceUuid }]}
        title={name || ' '}
        titleAside={<StatusPill latest={latest} />}
        meta={meta}
        actions={
          <DownloadButton onClick={csv.download} loading={csv.downloading} progress={csv.progress} disabled={!latest} />
        }
      />

      <RangeToolbar
        value={period}
        onChange={changePeriod}
        first={first}
        last={last}
        count={window ? samples.length : undefined}
      />

      {error !== undefined && !loading && (
        <Banner
          kind="error"
          action={
            <button type="button" onClick={reload}>
              {tc('retry')}
            </button>
          }
        >
          {tc('errors.generic')}
        </Banner>
      )}
      {csv.error !== undefined && <Banner kind="error">{tc('errors.csv')}</Banner>}
      {window?.truncated && <Banner kind="info">{tc('range.truncated', { count: window.readings.length })}</Banner>}

      {loading && !window ? (
        <div className="GeoDetailPage__loading">
          <Spinner />
        </div>
      ) : (
        window &&
        (samples.length === 0 ? (
          <EmptyPeriod />
        ) : (
          <>
            <section
              aria-label={t('route.label')}
              className="GeoDetailPage__map"
              title={t('route.aria', { name, count: samples.length, km: routeDistanceKm(samples).toFixed(2) })}
            >
              <div className="GeoDetailPage__map-head">
                <h2>{routeTitle}</h2>
                <div className="GeoDetailPage__legend">
                  <span>
                    <span aria-hidden="true" className="GeoDetailPage__legend-start" />
                    {t('route.start')}
                  </span>
                  <span>
                    <span aria-hidden="true" className="GeoDetailPage__legend-end" />
                    {t('route.last')}
                  </span>
                  <span>
                    <span aria-hidden="true" className="GeoDetailPage__legend-path" />
                    {t('route.path')}
                  </span>
                </div>
              </div>
              <RouteMap samples={samples} selected={selected} onSelect={setSelected} />
            </section>

            <RawDataTable samples={samples} selected={selected} onSelect={setSelected} />
          </>
        ))
      )}
    </Page>
  );
};

export default GeoDetailPage;
